// Rename plan -> safe bash scripts (forward + undo) with correct ordering.

function parentOf(path) {
  const i = path.lastIndexOf("/");
  return i === -1 ? "" : path.slice(0, i);
}

function shellQuote(s) {
  return "'" + String(s).replace(/'/g, "'\\''") + "'";
}

// Build ordered {oldPath,newPath} pairs. Deepest items first so a renamed
// parent folder never invalidates a child's path.
export function buildPairs(rows) {
  return rows
    .filter((r) => r.include && r.suggested && r.suggested !== r.original)
    .map((r) => {
      const path = r.path || r.original;
      const parent = parentOf(path);
      const newPath = parent ? `${parent}/${r.suggested}` : r.suggested;
      const depth = r.depth ?? path.split("/").length - 1;
      return { oldPath: path, newPath, depth };
    })
    .sort((a, b) => b.depth - a.depth);
}

// Ordered direct-rename steps (deepest-first) for native/adapter execution.
// Carries the live filesystem handle (_handle) so a Mac/native layer can move.
export function buildApplySteps(rows) {
  return rows
    .filter((r) => r.include && r.suggested && r.suggested !== r.original)
    .map((r) => ({
      id: r.id,
      oldBasename: r.original,
      newBasename: r.suggested,
      type: r.type,
      path: r.path || r.original,
      parent: r.parent ?? (r.path && r.path.includes("/") ? r.path.slice(0, r.path.lastIndexOf("/")) : ""),
      depth: r.depth ?? (r.path ? r.path.split("/").length - 1 : 0),
      handle: r._handle,
    }))
    .sort((a, b) => b.depth - a.depth);
}

// Reverse of a completed step list, in the correct order to undo safely
// (parents restored before their children).
export const reverseSteps = (steps) => steps.slice().reverse();

const HEADER = [
  "#!/bin/bash",
  "# Rename Studio AI — generated rename script",
  "# Review before running. Place next to the target folder and run: bash rename.sh",
  "# Renames deepest files/folders first, parent folders last.",
  "set -euo pipefail",
  "",
];

export function buildRenameScript(rows, dirName) {
  const pairs = buildPairs(rows);
  const lines = [...HEADER];
  if (dirName) lines.push(`# Root folder: ${dirName}`, "");
  pairs.forEach((p) =>
    lines.push(`mv -v -- ${shellQuote(p.oldPath)} ${shellQuote(p.newPath)}`)
  );
  lines.push("", `echo "Done. Renamed ${pairs.length} item(s)."`, "");
  return lines.join("\n");
}

// Undo = reverse the forward pairs and swap src/dst. Because forward pairs use
// original ancestor names and run deepest-first, undoing in reverse order
// reverts parents before their children, keeping every path valid.
export function buildUndoScript(rows, dirName) {
  const pairs = buildPairs(rows).slice().reverse();
  const lines = [
    "#!/bin/bash",
    "# Rename Studio AI — UNDO script",
    "# Reverses the matching rename.sh. Review before running.",
    "set -euo pipefail",
    "",
  ];
  if (dirName) lines.push(`# Root folder: ${dirName}`, "");
  pairs.forEach((p) =>
    lines.push(`mv -v -- ${shellQuote(p.newPath)} ${shellQuote(p.oldPath)}`)
  );
  lines.push("", `echo "Undo complete. Restored ${pairs.length} item(s)."`, "");
  return lines.join("\n");
}

export function downloadText(filename, text) {
  const blob = new Blob([text], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
